import { useCallback, useSyncExternalStore } from 'react';
import api from './api';
import { onSocket } from './socket';

/**
 * Colleagues' presence for the dots on their pictures (see
 * server/utils/presence.js for what each status means).
 *
 * One store for the whole page: every dot on screen asks for its person, the
 * asks are gathered into one request, changes arrive live over the socket
 * ('presence:update'), and everything shown is re-checked every 30 seconds -
 * which is how a call starting or ending shows up. A server without the
 * presence route simply leaves the dots off.
 */

const REFRESH_MS = 30 * 1000;
const BATCH_MS = 60;

const cache = new Map();      // userId -> { status, label, online, lastSeenAt }
const watchers = new Map();   // userId -> Set of listeners
const queued = new Set();
let batchTimer = null;
let refreshTimer = null;
let socketCleanup = null;

const notify = (id) => watchers.get(id)?.forEach((fn) => fn());

const store = (id, presence) => {
    cache.set(id, { ...cache.get(id), ...presence });
    notify(id);
};

const flush = async () => {
    batchTimer = null;
    const ids = [...queued];
    queued.clear();
    if (!ids.length) return;
    try {
        const { data } = await api.post('/employees/presence', { ids });
        for (const [id, presence] of Object.entries(data?.presence || {})) store(id, presence);
    } catch (err) {
        // Keep whatever was shown; the next refresh tries again.
    }
};

const ask = (id) => {
    queued.add(id);
    if (!batchTimer) batchTimer = setTimeout(flush, BATCH_MS);
};

const askAll = () => {
    for (const id of watchers.keys()) ask(id);
};

const start = () => {
    if (refreshTimer) return;
    refreshTimer = setInterval(() => {
        if (!document.hidden) askAll();
    }, REFRESH_MS);

    const offUpdate = onSocket('presence:update', (p) => {
        if (p?.userId) store(String(p.userId), p);
    });
    // Back from a dropped connection: changes may have been missed.
    const offConnect = onSocket('connect', askAll);
    socketCleanup = () => {
        offUpdate?.();
        offConnect?.();
    };
};

const stop = () => {
    clearInterval(refreshTimer);
    refreshTimer = null;
    socketCleanup?.();
    socketCleanup = null;
};

export const watchPresence = (userId, listener) => {
    const id = String(userId);
    if (!watchers.has(id)) {
        watchers.set(id, new Set());
        ask(id);
    }
    watchers.get(id).add(listener);
    start();
    return () => {
        const set = watchers.get(id);
        if (!set) return;
        set.delete(listener);
        if (!set.size) watchers.delete(id);
        if (!watchers.size) stop();
    };
};

export const getPresence = (userId) => (userId ? cache.get(String(userId)) || null : null);

/** For tests and logout: forget everything. */
export const resetPresence = () => {
    stop();
    cache.clear();
    watchers.clear();
    queued.clear();
    clearTimeout(batchTimer);
    batchTimer = null;
};

/** The person's presence, kept current; null until known (or for nobody). */
export const usePresence = (userId) => {
    const id = userId ? String(userId) : '';
    const subscribe = useCallback((fn) => (id ? watchPresence(id, fn) : () => { }), [id]);
    return useSyncExternalStore(subscribe, () => getPresence(id));
};
